<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router/composables'
import { StripeCheckoutPopup, useStripeCheckout } from '@/composables/payment'
import { useUserStore } from '@/stores'
import { useAxonPageView } from '@/composables/axon/useAxonPageView'
import { useAxonViewItem } from '@/composables/axon/useAxonViewItem'
import { genPlayTraceId } from '@/utils/playTraceId'
import { aliOssImageCompress } from '@/utils/aliOssImageCompress'

const route = useRoute()
const router = useRouter()
const userStore = useUserStore()
const { reportPageView } = useAxonPageView()

// 初始化时把 playTraceId 落到游戏页路由上（落地页跳转已带则复用），
// gameUrl 复制 route.query 即可带上，保证浏览器地址与游戏引擎 URL 一致
if (!route.query.playTraceId)
  router.replace({ query: { ...route.query, playTraceId: genPlayTraceId() } })

const coverUrl = computed(() => (route.query.coverUrl as string) || sessionStorage.getItem('gameCoverUrl') || '')
const compressedCoverUrl = computed(() => coverUrl.value ? aliOssImageCompress(coverUrl.value) : '')

const { reportViewItem } = useAxonViewItem()
function reportGameViewItem() {
  const contentId = (route.query.contentId as string) || ''
  if (!contentId)
    return
  reportViewItem({
    contentId,
    contentName: (route.query.contentName as string) || '',
    coverUrl: coverUrl.value,
  })
}

// ===== loading 背景：与详情页（BookPage.vowpath）同一套实现 =====
// 清晰封面 contain 顶对齐完整展示；其上叠 OSS 高斯模糊封面（cover），用纵向渐变遮罩只在下半部分显现
// 封面固定 9:16 顶对齐，全面屏上封面底缘约在 84%，遮罩终点下移到 80% 让封面标题尽量保持清晰
const BLUR_STOP_START = 66
const BLUR_STOP_END = 80

const loadingRef = ref<HTMLElement>()
const panelRef = ref<HTMLElement>()
const loadingHeight = ref(0)
const panelHeight = ref(0)

// 渐变终点取「80%」与「底部内容区顶缘」中更靠上的一个，保证进度条/文案落在模糊区内
const blurStops = computed(() => {
  const span = BLUR_STOP_END - BLUR_STOP_START
  let end = BLUR_STOP_END
  if (loadingHeight.value && panelHeight.value) {
    const panelTop = ((loadingHeight.value - panelHeight.value - 8) / loadingHeight.value) * 100
    end = Math.min(end, panelTop)
  }
  end = Math.max(end, span)
  return { start: end - span, end }
})

const bgStyle = computed(() => {
  const url = compressedCoverUrl.value || '/content/read/bg.jpg'
  return `background: url(${url}) no-repeat center top; background-size: contain;`
})

// 模糊封面走 OSS 处理（与详情页/APP 同参数：缩到 200 宽后高斯模糊）
const blurCoverUrl = computed(() => {
  if (!coverUrl.value)
    return ''
  const base = coverUrl.value.split('?')[0]
  return `${base}?x-oss-process=image/resize,w_200/blur,r_25,s_15/format,webp`
})

const blurBgStyle = computed(() => {
  if (!blurCoverUrl.value)
    return ''
  const grad = `linear-gradient(to bottom, transparent ${blurStops.value.start.toFixed(1)}%, #000 ${blurStops.value.end.toFixed(1)}%)`
  return `background: url(${blurCoverUrl.value}) no-repeat center top; background-size: cover;`
    + ` mask-image: ${grad}; -webkit-mask-image: ${grad};`
})

// 底部内容区高度随 tips 行数变化，实时测量以调整模糊层渐变位置
let resizeObserver: ResizeObserver | undefined
function observeLoadingLayout() {
  function update() {
    loadingHeight.value = loadingRef.value?.clientHeight || 0
    panelHeight.value = panelRef.value?.offsetHeight || 0
  }
  update()
  if (typeof ResizeObserver !== 'undefined') {
    resizeObserver = new ResizeObserver(update)
    if (loadingRef.value)
      resizeObserver.observe(loadingRef.value)
    if (panelRef.value)
      resizeObserver.observe(panelRef.value)
  }
}

const gameUrl = computed(() => {
  const params = new URLSearchParams()
  Object.entries(route.query).forEach(([key, value]) => {
    if (key !== 'coverUrl' && value)
      params.set(key, value as string)
  })
  return `/content/read/index.html?${params.toString()}`
})

const TIPS = [
  'Your choices shape the romance—who will win your heart?',
  'Every decision leads to a new twist in your story.',
  'Multiple endings await. Will you find true love?',
  'Swipe right or left? Your choices decide the romance.',
  'One story, countless paths. Where will your heart lead?',
  'Fall in love your way—pick your perfect partner.',
  'From first date to forever: you write the love story.',
  'Will you choose the charming stranger or the loyal friend?',
  'Every chapter holds secrets. Choose wisely.',
  'Your happy ending is just a choice away.',
  'Steamy scenes or sweet romance? You set the tone.',
  'No wrong answers, only different love stories.',
  'Replay to discover all the romantic possibilities.',
  'Love triangles, second chances, and hidden sparks.',
  'Flirt, tease, or play hard to get—it\'s up to you.',
  'Your choices change the chemistry with every character.',
  'Will you risk it all for passion or play it safe?',
  'Unlock secret scenes with the right decisions.',
  'Romance blooms in unexpected choices.',
  'The power is in your hands—write your own love saga.',
]

const iframeRef = ref<HTMLIFrameElement | null>(null)
const showLoading = ref(true)
const percent = ref(0)
const currentTip = ref('')

// ===== Stripe 内嵌支付（游戏内）=====
const {
  stripeClientSecret,
  stripeVisible,
  openStripeCheckout,
  closeStripeCheckout,
  setStripeVisible,
  verifyStripeReceipt,
} = useStripeCheckout()

// 容错解析：兼容字符串 / 已是对象 / 解析失败
function safeParse(raw: any) {
  if (raw == null)
    return null
  if (typeof raw === 'object')
    return raw
  try {
    return JSON.parse(raw)
  }
  catch {
    return null
  }
}

// H5 → 游戏：发送 command（本地联调用 '*'，生产需替换为真实 Unity origin）
function sendCommandToGame(commandId: string, data: Record<string, any> = {}) {
  const win = iframeRef.value?.contentWindow
  if (!win) {
    console.warn('[PAY-BRIDGE] iframe contentWindow 不存在，无法回传')
    return
  }
  const payload = {
    type: 'command',
    data: JSON.stringify({ command_id: commandId, data: JSON.stringify(data) }),
  }
  // iframe 内容与本页同源（/content/read/），按同源 origin 投递，避免使用通配 '*'
  win.postMessage(payload, window.location.origin)
}

// 游戏 → H5：收到支付请求
function handleU2HPayOrder(flutterPayDataRaw: any) {
  const payData = safeParse(flutterPayDataRaw)

  // 参数校验：解析失败或缺关键字段时不向后端发起下单，直接回传失败
  const product = payData?.product
  if (!payData || typeof payData !== 'object' || !product?.extendProductId) {
    console.error('[PAY-BRIDGE] 支付请求参数非法，已忽略:', payData)
    sendCommandToGame('h2u_pay_failed', {})
    return
  }

  startStripePay(payData)
}

// 游戏内发起 Stripe 内嵌支付
async function startStripePay(payData: any) {
  // product 为服务端返回的商品结构（ProductGroupItem），字段透传给内嵌接口
  const product = payData?.product || {}
  // successUrl/cancelUrl 后端为必填，需照常传；是否跳转由后端创建 Session 时
  // redirect_on_completion: 'never' 控制，前端不靠省略 url 规避
  const params = {
    ...product,
    contentId: payData?.contentId,
    contentChapterId: payData?.chapterId,
    memberCurrencyCode: 'usd',
    payType: 5, // stripe
    type: 1, // 阅读页面（游戏内容内）
    successUrl: window.location.href,
    cancelUrl: window.location.href,
  }
  try {
    // extendProductId 来自游戏透传的商品信息，验单时需回传
    await openStripeCheckout(params, product.extendProductId)
  }
  catch (e) {
    console.error('[PAY-BRIDGE] 创建 Stripe 内嵌支付失败:', e)
    sendCommandToGame('h2u_pay_failed', {})
  }
}

// 内嵌支付完成：验单后回传游戏结果
async function onGameStripeComplete() {
  const ok = await verifyStripeReceipt()
  closeStripeCheckout()
  sendCommandToGame(ok ? 'h2u_pay_success' : 'h2u_pay_failed', {})
}

// 用户主动关闭内嵌弹窗：回传取消
function onGameStripeCancel() {
  closeStripeCheckout()
  sendCommandToGame('h2u_pay_cancled', {})
}

let tipsTimer: ReturnType<typeof setInterval> | null = null
let fallbackTimer: ReturnType<typeof setTimeout> | null = null
let mockTimer: ReturnType<typeof setInterval> | null = null

function randomTip() {
  currentTip.value = TIPS[Math.floor(Math.random() * TIPS.length)]
}

function stopTips() {
  if (tipsTimer) {
    clearInterval(tipsTimer)
    tipsTimer = null
  }
}

function stopFallback() {
  if (fallbackTimer) {
    clearTimeout(fallbackTimer)
    fallbackTimer = null
  }
}

function stopMock() {
  if (mockTimer) {
    clearInterval(mockTimer)
    mockTimer = null
  }
}

function hideLoading() {
  stopFallback()
  stopMock()
  percent.value = 100
  setTimeout(() => {
    showLoading.value = false
    stopTips()
  }, 300)
}

function onGameMessage(event: MessageEvent) {
  // 仅接受本页 iframe 发来的消息，阻断任意窗口 postMessage 触发支付下单/跳转
  if (event.source !== iframeRef.value?.contentWindow)
    return

  const { type, percent: p } = event.data ?? {}
  if (!type)
    return

  switch (type) {
    case 'unity_progress':
      percent.value = p ?? 0
      break
    case 'addressables_progress':
      percent.value = p ?? 50
      if (p >= 100) {
        reportGameViewItem()
        setTimeout(hideLoading, 500)
      }
      break
    case 'return_to_home': {
      userStore.init({ force: true })
      // 返回书籍详情页；query 上无 contentId 时兜底回书城
      const contentId = (route.query.contentId as string) || ''
      if (contentId)
        router.replace({ name: 'book', params: { contentId } })
      else
        router.replace({ name: 'home' })
      break
    }
    case 'navigate_external':
      if (event.data.url)
        window.location.replace(event.data.url)
      break
    case 'command': {
      // 改进版协议：{ type:'command', data: CommandData 序列化字符串 }
      const cmd = safeParse(event.data.data)
      if (cmd?.command_id === 'u2h_pay_order')
        handleU2HPayOrder(cmd.data)
      break
    }
    case 'u2h_pay_order':
      // 旧版协议：直接 type 为 u2h_pay_order，data 为 FlutterPayData 字符串
      handleU2HPayOrder(event.data.data)
      break
  }
}

function onIframeLoad() {
  try {
    const iframePath = iframeRef.value?.contentWindow?.location?.pathname ?? ''
    if (iframePath.startsWith('/content/') && !iframePath.startsWith('/content/read/')) {
      const contentId = iframePath.replace('/content/', '')
      stopTips()
      showLoading.value = false
      router.replace({ name: 'book', params: { contentId } })
    }
  }
  catch {}
}

function onPopState() {
  window.removeEventListener('popstate', onPopState)
  userStore.init({ force: true })
  router.back()
}

onMounted(() => {
  reportPageView()
  randomTip()
  tipsTimer = setInterval(randomTip, 2000)
  window.addEventListener('message', onGameMessage)

  // iframe 全屏覆盖会劫持浏览器返回事件，推入一个守卫历史记录
  // 确保用户按返回时 popstate 能传递到父窗口
  history.pushState({ gameGuard: true }, '', location.href)
  window.addEventListener('popstate', onPopState)

  observeLoadingLayout()
})

onUnmounted(() => {
  stopTips()
  stopFallback()
  stopMock()
  window.removeEventListener('message', onGameMessage)
  window.removeEventListener('popstate', onPopState)
  resizeObserver?.disconnect()
})
</script>

<template>
  <div class="game-page">
    <Transition name="fade">
      <div v-if="showLoading" ref="loadingRef" class="loading-overlay">
        <div class="loading-bg absolute inset-0" :style="bgStyle" />
        <div class="loading-bg-blur absolute inset-0" :style="blurBgStyle" />
        <div class="spacer" />
        <div ref="panelRef" class="loading-panel">
          <div class="tips-text">
            {{ currentTip }}
          </div>
          <div class="tips-gap" />
          <div class="progress-container">
            <div class="progress-bar" :style="{ width: `${percent}%` }" />
            <div class="progress-text" :style="{ left: `${percent}%` }">
              {{ percent }}%
            </div>
          </div>
          <div class="loading-label">
            loading...
          </div>
        </div>
      </div>
    </Transition>
    <!-- 等 playTraceId 落到路由后再挂载，避免 router.replace 触发引擎二次加载 -->
    <iframe v-if="route.query.playTraceId" ref="iframeRef" :src="gameUrl" class="game-frame" allowfullscreen @load="onIframeLoad" />

    <!-- Stripe 内嵌支付弹窗（游戏内） -->
    <StripeCheckoutPopup
      :value="stripeVisible"
      :client-secret="stripeClientSecret"
      @input="setStripeVisible"
      @cancel="onGameStripeCancel"
      @complete="onGameStripeComplete"
    />
  </div>
</template>

<style lang="scss" scoped>
.game-page {
  position: fixed;
  inset: 0;
  background: #231F20;
}

.game-frame {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  border: none;
}

.loading-overlay {
  position: absolute;
  inset: 0;
  z-index: 10;
  display: flex;
  flex-direction: column;
  align-items: center;
  overflow: hidden;
  // 兜底底色：背景图未加载完时蒙层即保持不透明，避免下层引擎页 loading 透上来造成重叠
  background-color: #231F20;
  color: white;
  font-family: Arial, sans-serif;

  // 封面两层均只作背景，不接收交互；z-index -1 位于兜底底色之上、内容之下
  .loading-bg,
  .loading-bg-blur {
    z-index: -1;
    pointer-events: none;
  }
}

.spacer {
  margin-top: auto;
}

.loading-panel {
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.tips-text {
  max-width: 75%;
  font-size: 18px;
  font-weight: bold;
  text-align: center;
}

.tips-gap {
  margin-bottom: 3vh;
}

.progress-container {
  position: relative;
  width: 80%;
  max-width: 310px;
  background: #ffffff;
  border-radius: 20px;
  overflow: hidden;
  margin-bottom: 3vh;
}

.progress-bar {
  height: 14px;
  background: linear-gradient(to right, #C68BFF, #FF8DDC);
  border-radius: 20px;
  transition: width 0.2s ease;
}

.progress-text {
  position: absolute;
  top: 0;
  height: 14px;
  line-height: 14px;
  font-size: 12px;
  color: white;
  white-space: nowrap;
  transform: translateX(-110%);
  transition: left 0.2s ease;
  pointer-events: none;
}

.loading-label {
  margin-bottom: 5vh;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}
</style>
